import * as React from 'react';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Select — Figma node 166:39 family (Component 02).
 *
 * Native `<select>` styled to match the design system. For richer popovers
 * (search/multi-select), wrap a Radix Select with the same visual classes
 * or fall back to the legacy `@/components/ui/select`.
 *
 * Spec: height 36px, padding 7px 10px, r-md, border-1 ggc-border-gray-dark.
 * Disabled: bg-base + opacity-40 (the design uses 40% but we keep `disabled`
 * native cursor + reduced opacity for accessibility).
 */
export interface GgcSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Adds visual error state. */
  error?: boolean;
  /**
   * Visual size. `default` (38px) for forms; `sm` (30px) for dense table cells
   * and inline-edit fields.
   */
  size?: 'default' | 'sm';
  /** Extra classes applied to the outer wrapper span (controls width, etc.). */
  wrapperClassName?: string;
}

const baseClasses =
  'w-full appearance-none rounded-ggc-md border bg-white font-ggc text-ggc-main transition-colors focus:outline-none disabled:cursor-not-allowed disabled:bg-ggc-bg-base disabled:opacity-40';

const sizeClasses = {
  default: 'h-[38px] text-[12px] pl-[10px] pr-[28px] py-[7px]',
  sm: 'h-[30px] text-[11px] pl-[8px] pr-[24px] py-[3px]',
} as const;

const stateClasses = {
  default: 'border-ggc-border-gray-dark focus:border-ggc-cta-blue focus:ring-2 focus:ring-ggc-cta-blue/10',
  error: 'border-ggc-red ring-2 ring-ggc-red/10 focus:border-ggc-red',
};

const GgcSelect = React.forwardRef<HTMLSelectElement, GgcSelectProps>(
  ({ className, error, size = 'default', wrapperClassName, children, ...props }, ref) => {
    return (
      <span className={cn('relative inline-block w-[200px]', wrapperClassName)}>
        <select
          ref={ref}
          className={cn(baseClasses, sizeClasses[size], stateClasses[error ? 'error' : 'default'], className)}
          {...props}
        >
          {children}
        </select>
        <svg
          aria-hidden
          viewBox="0 0 8 5"
          className="pointer-events-none absolute right-[10px] top-1/2 size-[8px] -translate-y-1/2 text-ggc-text-gray"
          fill="currentColor"
        >
          <path d="M0 0h8L4 5z" />
        </svg>
      </span>
    );
  }
);
GgcSelect.displayName = 'GgcSelect';

export { GgcSelect };
